import { AfterContentChecked, AfterViewInit, Component, ContentChildren, inject, Input, OnDestroy, OnInit, QueryList, forwardRef, output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MenuEntryComponent } from '../menu-entry/menu-entry.component';
import { MenuService } from '../menu.service';
import { MatListItem, MatNavList } from "@angular/material/list";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";

/**
 * Composant d'entrée de menu dépliable
 * usage:
 * ```
 * <pegase-menu-entry-with-children [navTitle]="'texte du menu'" [matIcon]="route.data['matIcon']">
 *    <pegase-menu-entry [path]="'/ETAB00/notes-et-resultats'" [navTitle]="'Notes et résultats'" [matIcon]="'school'"></pegase-menu-entry>
 *    <pegase-menu-entry [path]="route.path" [navTitle]="route.data['navTitle']" [matIcon]="route.data['matIcon']"></pegase-menu-entry>
 * </pegase-menu-entry-with-children>
 * ```
 *
 */
@Component({
  selector: 'lib-pegase-menu-entry-with-children',
  templateUrl: './menu-entry-with-children.component.html',
  styleUrls: ['./menu-entry-with-children.component.scss'],
  standalone: true,
  imports: [
    MatListItem,
    MatTooltip,
    MatIcon,
    MatNavList
  ]
})
export class MenuEntryWithChildrenComponent implements AfterContentChecked, OnInit, OnDestroy, AfterViewInit {
  @ContentChildren(MenuEntryComponent) private childrenMenuEntryComponents!: QueryList<MenuEntryComponent>;
  @ContentChildren(forwardRef(() => MenuEntryWithChildrenComponent)) private childrenMenuEntryWithChildrenComponents!: QueryList<MenuEntryWithChildrenComponent>;

  @Input() navTitle!: string;
  @Input() navTooltip!: string;
  @Input() matIcon?: string;
  @Input() svgIcon?: string;
  @Input() routerLinkActiveOptions?: { exact: boolean }
  @Input() disabled?: boolean = false;
  @Input() displayIf?: boolean = true;
  readonly focusHandler = output<void>();
  public isOpened = false;
  public clickHandlerProcessing = false;
  public aChildPathMatchesActualRoute = false;
  private subscriptions: Subscription = new Subscription();
  private router = inject(Router);
  private menuService = inject(MenuService);
  ngAfterViewInit(): void {
    this.listenToChildrenFocus();
  }

  ngAfterContentChecked(): void {
    this.aChildPathMatchesActualRoute =
        this.childrenMenuEntryComponents.some((component: MenuEntryComponent) => !!component.path &&`/${component.path}` == this.router.url)
        || this.childrenMenuEntryWithChildrenComponents.some((component: MenuEntryWithChildrenComponent) => component.aChildPathMatchesActualRoute);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.menuService.closeAllMenuEntriesWithChildrenObservable().subscribe(() => {
        if (this.childrenMenuEntryWithChildrenComponents?.length === 0 || !this.childrenMenuEntryWithChildrenComponents.some(c => c.clickHandlerProcessing)) {
          this.isOpened = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
      this.subscriptions.unsubscribe();
  }

  public clickHandler(): void {
    this.clickHandlerProcessing = true;
    if (!this.menuService.isMenuOpened) {
      this.menuService.closeAllMenuEntriesWithChildren();
      this.isOpened = true;
      if (this.childrenMenuEntryComponents.length > 0) {
        this.router.navigate([this.childrenMenuEntryComponents.toArray()[0].path]);
      } else if (this.childrenMenuEntryWithChildrenComponents.length > 0) {
        this.childrenMenuEntryWithChildrenComponents.toArray()[0].clickHandler()
      }
      this.menuService.openMenu();
    } else {
      if (this.isOpened) {
        this.menuService.closeAllMenuEntriesWithChildren();
      } else {
        this.menuService.closeAllMenuEntriesWithChildren();
        this.isOpened = true;
      }
    }
    this.clickHandlerProcessing = false;
  }

  private listenToChildrenFocus() {
    this.childrenMenuEntryComponents?.map((item: MenuEntryComponent) => {
      this.handleChildFocus(item);
    });
    this.childrenMenuEntryWithChildrenComponents?.map((item: MenuEntryWithChildrenComponent) => {
      this.handleChildFocus(item);
    });
  }

  /**
   * Si un enfant prend le focus, le menu s'ouvre (RGAA, navigation clavier)
   */
  private handleChildFocus(child: MenuEntryComponent|MenuEntryWithChildrenComponent) {
    this.subscriptions.add(
      child.focusHandler.subscribe(() => {
        this.isOpened = true;
        // TODO: The 'emit' function requires a mandatory void argument
        this.focusHandler.emit();
      })
    );
  }
}
